import { createEffect, createMemo, createSignal, For, onCleanup, Show } from "solid-js"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

type ChartConfig = {
  xKey: string
  series: Array<{ key: string; label: string; color?: string }>
}

type ChartType = "line" | "bar" | "doughnut" | "pie" | "radar" | "polarArea"

type ChartData = {
  labels: string[]
  series: Array<{ key: string; label: string; data: number[]; color?: string }>
}

const FALLBACK_COLORS = ["#3b82f6", "#16a34a", "#f59e0b", "#ef4444", "#8b5cf6", "#14b8a6"]

export function ChartWidget(props: {
  data: ChartData
  config?: ChartConfig
  chartType?: ChartType
  options?: Record<string, unknown>
}) {
  const [canvas, setCanvas] = createSignal<HTMLCanvasElement>()
  let chart: Chart | undefined
  const chartType = createMemo(() => props.chartType ?? "line")
  const configSeries = createMemo(() => props.config?.series ?? [])
  const polarAreaChart = createMemo(() => chartType() === "polarArea")
  const displaySeries = createMemo(() => (polarAreaChart() ? props.data.series.slice(0, 1) : props.data.series))
  const hasValues = createMemo(() => displaySeries().some((series) => series.data.some((value) => value !== 0)))
  const seriesColors = createMemo(() =>
    props.data.series.map(
      (series, index) =>
        series.color ??
        configSeries().find((entry) => entry.key === series.key)?.color ??
        FALLBACK_COLORS[index % FALLBACK_COLORS.length],
    ),
  )
  const segmentedChart = createMemo(() => chartType() === "doughnut" || chartType() === "pie" || polarAreaChart())
  const radialChart = createMemo(() => chartType() === "radar" || polarAreaChart())
  const segmentedColors = (seriesIndex: number) =>
    props.data.labels.map((_, labelIndex) =>
      labelIndex === 0
        ? seriesColors()[seriesIndex]
        : FALLBACK_COLORS[(seriesIndex + labelIndex) % FALLBACK_COLORS.length],
    )
  const datasets = createMemo(() =>
    displaySeries().map((series, index) => ({
      label: series.label,
      data: series.data,
      borderColor: seriesColors()[index],
      backgroundColor:
        chartType() === "line"
          ? `${seriesColors()[index]}33`
          : segmentedChart()
            ? segmentedColors(index)
            : seriesColors()[index],
      tension: chartType() === "line" ? 0.35 : undefined,
      fill: chartType() === "line",
    })),
  )

  createEffect(() => {
    if (!hasValues()) {
      chart?.destroy()
      chart = undefined
      return
    }
    const node = canvas()
    if (!node) return
    chart?.destroy()
    chart = new Chart(node, {
      type: chartType(),
      data: {
        labels: props.data.labels,
        datasets: datasets(),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display:
              displaySeries().length > 1 ||
              chartType() === "doughnut" ||
              chartType() === "pie" ||
              polarAreaChart(),
            position: "bottom",
          },
        },
        scales:
          chartType() === "doughnut" || chartType() === "pie"
            ? undefined
            : radialChart()
              ? {
                  r: {
                    beginAtZero: true,
                  },
                }
              : {
                x: {
                  grid: {
                    display: false,
                  },
                },
                y: {
                  beginAtZero: true,
                },
              },
        ...props.options,
      },
    })
  })

  onCleanup(() => chart?.destroy())

  return (
    <div class="flex flex-col gap-3">
      {hasValues() ? (
        <>
          <div class="relative h-64 min-h-64 w-full" data-chart-js={chartType()}>
            <canvas
              ref={(node) => {
                setCanvas(node)
              }}
            />
          </div>
          <Show when={displaySeries().length > 1}>
            <div class="flex flex-wrap items-center justify-center gap-4">
              <For each={displaySeries()}>
                {(series, index) => (
                  <div class="flex items-center gap-1.5">
                    <div class="h-3 w-3 rounded-sm" style={{ background: seriesColors()[index()] }} />
                    <span class="text-xs text-text-dimmed">{series.label}</span>
                  </div>
                )}
              </For>
            </div>
          </Show>
        </>
      ) : (
        <div
          data-dashboard-empty-state=""
          class="flex min-h-24 items-center justify-center rounded-lg border border-dashed border-border-weak-base bg-background-secondary/65 px-3 py-4 text-center text-xs text-text-dimmed"
        >
          No chart data yet
        </div>
      )}
    </div>
  )
}
